import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Chronological order: latest event date first (e.g. May → April → March). */
export function sortEventsByDateDesc(events) {
  return [...events].sort(
    (a, b) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime()
  );
}

/**
 * County page: upcoming events from the current month onward.
 * Current month with 3+ events → all of that month; otherwise fill to 3 from later months.
 */
export function selectCountyUpcomingDisplayEvents(events, referenceDate = new Date()) {
  const startOfToday = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  const upcoming = [...events]
    .filter((event) => new Date(event.event_date) >= startOfToday)
    .sort(
      (a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime()
    );

  if (upcoming.length === 0) {
    return [];
  }

  let year = referenceDate.getFullYear();
  let month = referenceDate.getMonth();
  const result = [];

  for (let i = 0; i < 24; i++) {
    const monthEvents = upcoming.filter((event) => {
      const d = new Date(event.event_date);
      return d.getFullYear() === year && d.getMonth() === month;
    });

    if (i === 0 && monthEvents.length >= 3) {
      return monthEvents;
    }

    if (i === 0) {
      result.push(...monthEvents);
    } else {
      const needed = 3 - result.length;
      result.push(...monthEvents.slice(0, needed));
    }

    if (result.length >= 3) {
      break;
    }

    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }

  return result;
}

const isSameCalendarDay = (dateA, dateB) => {
  const a = new Date(dateA);
  const b = new Date(dateB);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

/** Events on the selected calendar day, or upcoming list when that day has none. */
export function getCalendarPanelEvents(events, selectedDate, limit = 4) {
  const sorted = sortEventsByDateDesc(events);
  const onSelectedDay = sorted.filter((event) =>
    isSameCalendarDay(event.event_date, selectedDate)
  );
  if (onSelectedDay.length > 0) {
    return onSelectedDay;
  }
  return sorted.slice(0, limit);
}

/** Day-picker matcher: highlight days that have at least one event. */
export function hasEventOnDay(events) {
  const dayKeys = new Set(
    events.map((event) => {
      const d = new Date(event.event_date);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    })
  );
  return (date) =>
    dayKeys.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
}

/** Events featured on the Centenary page — must exist in the main events API */
export const CENTENARY_EVENT_TITLES = [
  'Planning Surgery',
  'Climate Change Webinar',
  'Telephone Box Competition',
];

export const CENTENARY_EVENTS_FALLBACK = [
  {
    id: 'centenary-1',
    title: 'Planning Surgery',
    day: '29',
    month: 'MAY',
    time: '4:00pm – 5:00pm',
    type: 'Workshop',
  },
  {
    id: 'centenary-2',
    title: 'Climate Change Webinar',
    day: '8',
    month: 'APR',
    time: '6:30pm – 8:00pm',
    type: 'Webinar',
  },
  {
    id: 'centenary-3',
    title: 'Telephone Box Competition',
    day: '',
    month: '',
    time: 'See competition page',
    type: 'Competition',
  },
];

const DISPLAY_TYPE_BY_TYPE = {
  walk: 'Guided Walk',
  volunteer: 'Volunteer Day',
  workshop: 'Workshop',
  community: 'Community Event',
};

export function mapCentenaryEventCard(event) {
  const date = new Date(event.event_date);
  const hideDay =
    event.title !== 'Telephone Box Competition' &&
    Boolean(event.time_label && event.time_label.includes('competition'));

  return {
    id: event.id,
    title: event.title,
    day: hideDay ? '' : String(date.getDate()),
    month: hideDay ? '' : date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
    time:
      event.time_label ||
      date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    type: event.display_type || DISPLAY_TYPE_BY_TYPE[event.type] || 'Event',
  };
}

export async function fetchAllEvents() {
  const response = await axios.get(`${API}/events`, {
    params: { upcoming_only: false, limit: 50 },
  });
  return sortEventsByDateDesc(response.data || []);
}

export function getCentenaryEventsFromList(allEvents) {
  const titleSet = new Set(CENTENARY_EVENT_TITLES);
  return CENTENARY_EVENT_TITLES.map((title) =>
    allEvents.find((event) => event.title === title)
  )
    .filter(Boolean)
    .map(mapCentenaryEventCard);
}
